import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MAX_QR_IMAGE_BYTES,
  MAX_QR_TEXT,
  qrModules,
  type ReceiptQrConfig,
} from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { CustomerZonePrices } from './CustomerZonePrices';
import { MerchantOpenApiDialog } from './MerchantOpenApiDialog';
import type { PickupPointRow } from '../lib/types';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface ProofStage {
  required: boolean;
  mode: 'PER_BATCH' | 'PER_ORDER';
  minPhotos: number;
  maxPhotos: number;
  hint?: string;
  requireSignature: boolean;
  allowSkipWithReason: boolean;
}

export interface CustomerRow {
  id: string;
  code: string;
  name: string;
  /** 小票上印的名字。空 = 不印 */
  printAlias: string | null;
  /** 小票上印的二维码。null = 不印 */
  receiptQr: ReceiptQrConfig | null;
  contact: string | null;
  phone: string | null;
  billingCycle: string;
  isActive: boolean;
  proofConfig: { pickup?: ProofStage; delivery?: ProofStage };
  orderCount: number;
  pickupPointCount: number;
}

const DEFAULT_STAGE: ProofStage = {
  required: true,
  mode: 'PER_BATCH',
  minPhotos: 1,
  maxPhotos: 3,
  requireSignature: false,
  allowSkipWithReason: false,
};

/**
 * 商家。
 *
 * ★ 原始需求里那条「取货照片是商品照片,证明货收到了,按商家开关,
 *   后台可以关掉」—— 机制早就实现了(四级优先级,商家是权威来源),
 *   但一直没有入口,只能靠 seed 建商家。等于那条需求只做了一半。
 */
export function CustomerSettings({
  canManage,
  canManageApi = false,
  points = [],
}: {
  canManage: boolean;
  /** 能不能配开放接口(setting:manage)—— Secret 能以这家商家的名义下单 */
  canManageApi?: boolean;
  points?: PickupPointRow[];
}) {
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [editing, setEditing] = useState<CustomerRow | 'new' | null>(null);
  /** 正在看哪一家的分区价 */
  const [pricingFor, setPricingFor] = useState<CustomerRow | null>(null);
  const [openApiFor, setOpenApiFor] = useState<CustomerRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 正在等二次确认的那家 */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * ★ 删除是软删除:这家的历史单、账单、结算都还要查得到。
   *   真删了的话,上个月的对账单上会出现一行没有商家名字的钱。
   *
   * ★ 拒绝的理由由后端给(还有单没送完、还挂着取货点),
   *   这里**原样显示**——自己编一句「删除失败」的话,
   *   人不知道该先去做什么才能删掉。
   */
  async function remove(c: CustomerRow) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/customers/${c.id}`, { method: 'DELETE' });
      setConfirming(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '删不掉');
    } finally {
      setBusy(false);
    }
  }

  const load = useCallback(async () => {
    try {
      setRows(await api<CustomerRow[]>('/customers?includeInactive=true'));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到商家列表');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">商家</h2>
        {canManage && (
          <button
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
          >
            + 新建商家
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">还没有商家。商家决定了取货时要不要拍照。</p>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                        bg-white dark:bg-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-3 text-left font-medium">商家</th>
                <th className="p-3 text-left font-medium">联系人</th>
                <th className="p-3 text-left font-medium">取货拍照</th>
                <th className="p-3 text-right font-medium">订单</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 dark:border-slate-700/50">
                  <td className="p-3">
                    <div className="text-slate-900 dark:text-slate-100">
                      {c.name}
                      {!c.isActive && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-100
                                         dark:bg-slate-700 text-slate-500">
                          已停用
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">{c.code}</div>
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-400">
                    {c.contact ?? '—'}
                    {c.phone && <div className="text-xs text-slate-400">{c.phone}</div>}
                  </td>
                  {/* ★ 列表上直接显示拍不拍照、按批还是按单 ——
                         这是司机每天要多花多少时间的直接决定因素 */}
                  <td className="p-3">
                    <ProofSummary stage={c.proofConfig.pickup} />
                  </td>
                  <td className="p-3 text-right tabular-nums text-slate-600 dark:text-slate-400">
                    {c.orderCount}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canManageApi && (
                      <button
                        onClick={() => setOpenApiFor(c)}
                        className="mr-3 text-brand-600 dark:text-brand-500 hover:underline"
                      >
                        开放接口
                      </button>
                    )}
                    {canManage && (
                      <>
                        <button
                          onClick={() => setEditing(c)}
                          className="text-brand-600 dark:text-brand-500 hover:underline"
                        >
                          编辑
                        </button>
                        {/*
                          ★ 分区价放在商家这一行上。
                            它本来只在「计价」页有一个带下拉框的入口 ——
                            而人是**按商家在想事情**的:刚看完这家的取货要求,
                            顺手就该能看这家的价,而不是换一页再把他名字找一遍。
                        */}
                        <button
                          onClick={() => setPricingFor(c)}
                          className="ml-3 text-brand-600 dark:text-brand-500 hover:underline"
                        >
                          分区价
                        </button>
                        {/*
                          ★ 删除要**当场二次确认**,而且要说清后果。
                            这不是 window.confirm:那个弹窗会挡住整页,
                            而且只有一句「确定吗」——人根本不知道自己在删什么。
                        */}
                        {confirming === c.id ? (
                          <span className="ml-3 inline-flex items-center gap-2">
                            <span className="text-xs text-slate-500">删「{c.name}」?</span>
                            <button
                              onClick={() => void remove(c)}
                              disabled={busy}
                              className="text-red-600 dark:text-red-400 hover:underline
                                         disabled:opacity-40"
                            >
                              {busy ? '删除中…' : '确定'}
                            </button>
                            <button
                              onClick={() => setConfirming(null)}
                              className="text-slate-500 hover:underline"
                            >
                              算了
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              setError(null);
                              setConfirming(c.id);
                            }}
                            aria-label={`删除 ${c.name}`}
                            className="ml-3 text-slate-400 hover:text-red-600 hover:underline"
                          >
                            删除
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pricingFor && (
        <div
          className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4"
          onClick={() => setPricingFor(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${pricingFor.name} 的分区价`}
            className="w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white p-5
                       dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {pricingFor.name} · 各大区基础价
              </h3>
              <button
                onClick={() => setPricingFor(null)}
                className="text-sm text-slate-500 hover:underline"
              >
                关闭
              </button>
            </div>
            {/* 和「计价」页那一屏是同一个组件,只是锁定到这一家 */}
            <CustomerZonePrices canManage={canManage} fixedCustomerId={pricingFor.id} compact />
          </div>
        </div>
      )}

      {openApiFor && (
        <MerchantOpenApiDialog
          customer={{ id: openApiFor.id, name: openApiFor.name }}
          points={points}
          onClose={() => setOpenApiFor(null)}
        />
      )}

      {editing && (
        <CustomerForm
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function ProofSummary({ stage }: { stage?: ProofStage }) {
  if (!stage || !stage.required) {
    return <span className="text-slate-500">不用拍</span>;
  }
  return (
    <span className="text-slate-700 dark:text-slate-300">
      要拍 {stage.minPhotos} 张
      <span className="text-slate-400">
        {' · '}
        {stage.mode === 'PER_BATCH' ? '整批一张' : '每单一张'}
      </span>
    </span>
  );
}

function CustomerForm({
  existing,
  onClose,
  onSaved,
}: {
  existing?: CustomerRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    printAlias: existing?.printAlias ?? '',
    contact: existing?.contact ?? '',
    phone: existing?.phone ?? '',
    isActive: existing?.isActive ?? true,
  });
  /*
   * ★ 已有商家「没有取货拍照配置」= 明确不要拍,不是「还没配过」。
   *
   *   原来两种情况都回落到 DEFAULT_STAGE(required: true)——
   *   于是取消勾选、保存(库里确实删掉了)、再打开,勾又出现了。
   *   人看到的是「取消保存不了」,而实际上保存一直是成功的:
   *   骗人的是重新打开时的默认值。
   *
   *   新建商家仍然默认要拍照 —— 那才是「还没配过」。
   */
  const [pickup, setPickup] = useState<ProofStage>(() =>
    existing
      ? (existing.proofConfig?.pickup ?? { ...DEFAULT_STAGE, required: false })
      : { ...DEFAULT_STAGE },
  );
  /*
   * ★ 二维码那一块的状态**分成三个**:方式、文本、图片。
   *
   *   不用一个 `ReceiptQrConfig | null` 直接当状态,是为了让人来回切方式时
   *   **别把刚填的东西弄丢**:选了图片、传了一张、又切回文本看一眼、
   *   再切回图片 —— 那张图还在。合成一个状态的话每切一次就清一次,
   *   而他会以为是上传失败了。
   *
   *   存回去的时候才按 `qrMode` 挑出那一个(见 save),所以库里
   *   永远只有一种方式生效 —— 界面上宽松,数据上二选一。
   */
  const [qrMode, setQrMode] = useState<'off' | 'TEXT' | 'IMAGE'>(
    existing?.receiptQr?.mode ?? 'off',
  );
  const [qrText, setQrText] = useState(
    existing?.receiptQr?.mode === 'TEXT' ? existing.receiptQr.text : '',
  );
  const [qrImage, setQrImage] = useState(
    existing?.receiptQr?.mode === 'IMAGE' ? existing.receiptQr.image : '',
  );
  const [qrErr, setQrErr] = useState<string | null>(null);

  /**
   * 这段内容会编成多大的码 —— 当场告诉他。
   *
   * ★ 为什么值得摆出来:二维码的密度是跟内容长度走的,而人填的时候
   *   完全感觉不到这件事。一段长网址编出来是 57×57,印在 28mm 见方里
   *   一格不到 3 个打印点 —— 码印得清清楚楚,**就是扫不动**。
   *   那种失败没有任何提示,他会怪打印机、怪手机,唯独不会想到内容太长。
   */
  const qrInfo = useMemo(() => {
    if (qrMode !== 'TEXT' || !qrText.trim()) return null;
    const m = qrModules(qrText);
    if (!m) return { n: 0, cell: 0, tooLong: qrText.length > MAX_QR_TEXT };
    // 默认「大」那一档是 224 点,外加规范要求的 4 模块静区
    const cell = Math.floor(224 / (m.length + 8));
    return { n: m.length, cell, tooLong: false };
  }, [qrMode, qrText]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 选了一张图:缩小、铺白底、转成 data: URI。
   *
   * ★ 为什么一定要在**浏览器里**先缩:这张图是内联在**每一张**小票里的,
   *   而后台打印一次能发五十张。原图直接存的话,一张手机截图
   *   就能让打印任务的 body 超过服务端上限 —— 而那时候的现象是
   *   「打印失败」,跟「那张二维码太大」之间没有任何看得出来的联系。
   *
   * ★ 为什么要**铺白底**:PNG 的透明区在画布上取到的是 (0,0,0,0),
   *   而蓝牙那条路按亮度二值化时不看 alpha —— 透明会被当成**黑**。
   *   现象是打出来一整块黑方块,中间隐约有个码。
   */
  async function pickImage(file: File): Promise<void> {
    setQrErr(null);
    try {
      const bitmap = await createImageBitmap(file);
      const MAX_SIDE = 384;
      const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);
      const uri = canvas.toDataURL('image/png');
      if (uri.length > MAX_QR_IMAGE_BYTES) {
        setQrErr(
          `这张图缩完还有 ${Math.round(uri.length / 1024)}KB —— 它要跟着每一张小票走,` +
            '请换一张干净的二维码截图(别传相片)',
        );
        return;
      }
      setQrImage(uri);
    } catch {
      setQrErr('这张图读不出来 —— 换一张试试(支持 PNG / JPG)');
    }
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: form.name,
        // 空字符串要发 null,不能发 undefined —— 前者是「清掉」,后者是「别动」
        printAlias: form.printAlias.trim() || null,
        contact: form.contact || undefined,
        phone: form.phone || undefined,
        isActive: form.isActive,
        /*
         * ★ **送达那一段要原样带回去**。
         *
         *   这一屏只编辑「取货拍照」,而保存时写的是整个 proofConfig ——
         *   原来的写法把 `delivery` 整段丢掉了:
         *   谁在这儿改一次取货设置,这个商家的**送达拍照要求就没了**,
         *   而界面上没有任何地方提到过 delivery,没人会把两件事联系起来。
         *   (本店就是这么被清掉的,连累四条不相干的用例一起红。)
         *
         *   不要拍照时不写 `pickup` —— 写 required:false 和不写效果一样,
         *   但留着一堆没用的字段会让人以为哪里还配着什么。
         */
        proofConfig: {
          ...(existing?.proofConfig ?? {}),
          ...(pickup.required ? { pickup } : { pickup: undefined }),
        },
        /*
         * ★ 按方式挑出**一个**,另一个连带着扔掉。
         *   两个都发过去的话接口会拒(那边是判别联合 + strict),
         *   而在这儿挑干净比让人对着一句校验错发愣要好。
         *
         * ★ 选了方式却没填内容,当作「不印」——
         *   发一个空的 TEXT 过去只会换回一句「请填写二维码内容」,
         *   而他心里想的是「我还没想好,先存别的」。
         */
        receiptQr:
          qrMode === 'TEXT' && qrText.trim()
            ? { mode: 'TEXT' as const, text: qrText.trim() }
            : qrMode === 'IMAGE' && qrImage
              ? { mode: 'IMAGE' as const, image: qrImage }
              : null,
      };
      if (existing) {
        await api(`/customers/${existing.id}`, { method: 'PATCH', body });
      } else {
        await api('/customers', { method: 'POST', body: { ...body, code: form.code } });
      }
      await onSaved();
    } catch (e) {
      /*
       * ★ 存储没配好的话,**把那个勾退回去**,不只是报一句错。
       *
       *   服务端已经拒了,界面上却还勾着 —— 他会以为存下了,
       *   然后等着司机拍照,而司机那边什么都没变。
       *   退回去之后,界面和服务端说的是同一件事。
       *
       *   按错误码判,不靠匹配文案:那句话改一个字这里就失效了。
       */
      if (e instanceof ApiFailure && e.code === 'STORAGE_NOT_READY') {
        setPickup((p) => ({ ...p, required: false }));
      }
      setError(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="商家"
        className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {existing ? `编辑 ${existing.name}` : '新建商家'}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {!existing && (
          <Field label="代号" hint="建好之后不能改 —— 导入的表里会用它对上商家">
            <input
              aria-label="代号"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className={INPUT}
            />
          </Field>
        )}

        <Field label="名称">
          <input
            aria-label="名称"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={INPUT}
          />
        </Field>

        {/*
          ★ 打印别名 —— 小票最上面那一行。
            和「名称」分开:台账里是全称,而贴在框上要的是分拣时
            一眼认出来的那几个字。不填就一个字都不印,票面和现在一样。
        */}
        <Field label="打印别名" hint="小票最上面印这个,方便分拣时认出是哪家的;不填就不印">
          <input
            aria-label="打印别名"
            value={form.printAlias}
            onChange={(e) => setForm({ ...form, printAlias: e.target.value })}
            placeholder="三号仓"
            className={INPUT}
          />
        </Field>

        {/*
          ★ 小票二维码 —— 印在打印别名**上面**。

            两种来源二选一,用单选而不是「填哪个算哪个」:
            两个输入框摆在那儿、谁非空谁生效的话,人改过一次之后
            分不清现在印的是哪个,而他要到打出来才知道。
        */}
        <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-2">
          <span className="block text-sm text-slate-600 dark:text-slate-400">小票二维码</span>
          {/*
            ★ 这一屏只管「填什么」,不管「印不印」。
            
              两件事分开是有道理的:填什么是这个商家的属性(他的收款码、
              他的公众号),而这叠票上印不印是眼下这一次打印的事 ——
              同一个商家,贴货架的票要印码,内部留存那张不需要。
              开关放在这儿的话,他得先来改商家、打完再改回来。

              所以必须把这句话写在这儿:填完这里**还不会印**,
              不说的话他填完就去打了,打出来没有码,而他会回来
              反复检查自己是不是填错了。
          */}
          <p className="text-xs text-slate-400">
            印在小票最下面、打印别名上面。扫码的人手指压住的正好是码下面那行店名。
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400">
            填在这里还不会印 —— 要印的话,在<strong>打印小票的弹窗</strong>里把
            「商家二维码」选一个尺寸。两边都设了才印。
          </p>

          <div className="flex gap-4 text-sm text-slate-900 dark:text-slate-100">
            {(
              [
                ['off', '不印'],
                ['TEXT', '填内容'],
                ['IMAGE', '传图片'],
              ] as const
            ).map(([v, label]) => (
              <label key={v} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="qrMode"
                  aria-label={label}
                  checked={qrMode === v}
                  onChange={() => {
                    setQrMode(v);
                    setQrErr(null);
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          {qrMode === 'TEXT' && (
            <Field label="二维码内容" hint="网址、收款码内容都行。打印时现生成">
              <textarea
                aria-label="二维码内容"
                value={qrText}
                onChange={(e) => setQrText(e.target.value)}
                rows={2}
                maxLength={MAX_QR_TEXT}
                placeholder="https://example.com/shop/123"
                className={INPUT.replace('h-10', 'h-auto py-2')}
              />
            </Field>
          )}

          {/*
            ★ 把「会编成多密」当场说出来。
              内容越长格子越小,而人填的时候完全感觉不到这件事 ——
              等他发现时已经打了一叠扫不动的票,
              而他会怪打印机、怪手机,唯独不会想到是内容太长。
          */}
          {qrInfo && qrInfo.n > 0 && (
            <p
              className={
                qrInfo.cell < 3 ? 'text-xs text-amber-600 dark:text-amber-400' : 'text-xs text-slate-400'
              }
            >
              {qrInfo.cell < 3
                ? `会编成 ${qrInfo.n}×${qrInfo.n} 的码,每格只有 ${qrInfo.cell} 个打印点 —— 印在小票上多半扫不动,建议把内容缩短(比如换成短链)`
                : `会编成 ${qrInfo.n}×${qrInfo.n} 的码,每格 ${qrInfo.cell} 个打印点,够扫`}
            </p>
          )}

          {qrMode === 'IMAGE' && (
            <div className="space-y-2">
              <input
                type="file"
                aria-label="二维码图片"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void pickImage(f);
                }}
                className="block w-full text-sm text-slate-600 dark:text-slate-400"
              />
              {qrImage ? (
                <div className="flex items-center gap-3">
                  <img
                    src={qrImage}
                    alt="二维码预览"
                    className="w-20 h-20 border border-slate-300 dark:border-slate-600 bg-white"
                  />
                  <div className="text-xs text-slate-400 space-y-1">
                    <div>已存这一张({Math.round(qrImage.length / 1024)}KB)</div>
                    <button
                      type="button"
                      onClick={() => setQrImage('')}
                      className="text-red-600 dark:text-red-400"
                    >
                      换一张
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  选一张二维码截图。会自动缩到 384px 再存 —— 它要跟着每一张小票走。
                </p>
              )}
            </div>
          )}

          {qrErr && <p className="text-xs text-red-600 dark:text-red-400">{qrErr}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="联系人">
            <input
              aria-label="联系人"
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              className={INPUT}
            />
          </Field>
          <Field label="电话">
            <input
              aria-label="电话"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={INPUT}
            />
          </Field>
        </div>

        {/* ── 取货拍照 ── */}
        <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
          <label className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              aria-label="取货要拍照"
              checked={pickup.required}
              onChange={(e) => setPickup({ ...pickup, required: e.target.checked })}
            />
            <span className="text-slate-900 dark:text-slate-100">取货时要拍商品照片</span>
          </label>
          {/*
            ★ 说清这是「证明货收到了」,而不是随手拍一张。
              不说的话,司机拍的会是店门口、马路、自己的鞋。
          */}
          <p className="text-xs text-slate-400">
            照片是「货确实收到了」的凭证。关掉之后,这个商家的单司机不用拍就能取货。
          </p>

          {pickup.required && (
            <div className="mt-3 space-y-3 pl-6">
              <Field
                label="拍几次"
                hint={
                  pickup.mode === 'PER_BATCH'
                    ? '整批一张:一次取 30 单也只拍一次'
                    : '★ 每单一张:一次取 30 单,司机要在店里站着拍 30 次'
                }
              >
                <select
                  aria-label="拍几次"
                  value={pickup.mode}
                  onChange={(e) =>
                    setPickup({ ...pickup, mode: e.target.value as ProofStage['mode'] })
                  }
                  className={INPUT}
                >
                  <option value="PER_BATCH">整批一张</option>
                  <option value="PER_ORDER">每单一张</option>
                </select>
              </Field>

              <Field label="最少几张">
                <input
                  aria-label="最少几张"
                  type="number"
                  min={1}
                  max={10}
                  value={pickup.minPhotos}
                  onChange={(e) =>
                    setPickup({ ...pickup, minPhotos: Number(e.target.value) || 1 })
                  }
                  className={INPUT}
                />
              </Field>

              <Field label="给司机的提示" hint="显示在拍照那一步。写清楚要拍什么">
                <input
                  aria-label="给司机的提示"
                  value={pickup.hint ?? ''}
                  onChange={(e) => setPickup({ ...pickup, hint: e.target.value })}
                  placeholder="例:拍清楚包装上的单号"
                  className={INPUT}
                />
              </Field>

              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  aria-label="允许说明原因后跳过"
                  className="mt-1"
                  checked={pickup.allowSkipWithReason}
                  onChange={(e) =>
                    setPickup({ ...pickup, allowSkipWithReason: e.target.checked })
                  }
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  允许司机填个原因跳过
                  {/* ★ 不允许跳过的后果要说明白 */}
                  <span className="block text-xs text-slate-400">
                    不允许的话,手机拍不了照(存储满、相机坏)就交不了这一单 ——
                    司机只能打电话找调度
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* ★ 总开关一票否决,必须写出来 —— 否则会有人在这里配了半天,
               发现司机端根本不弹拍照,而问题在设置的另一处 */}
        <p className="text-xs text-slate-400 pt-1">
          这里配的受「设置 → 功能开关」里的拍照总开关约束:总开关关掉的话,
          商家配了也不生效。
        </p>

        {existing && (
          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              aria-label="启用"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              启用
              <span className="text-slate-400"> —— 停用后不影响已有订单</span>
            </span>
          </label>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !form.name || (!existing && !form.code)}
            className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

const INPUT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}
